import { useEffect, useRef, useState } from 'react';
import type { ChangeEvent, CSSProperties, SyntheticEvent } from 'react';
import { NavLink, Outlet, Link, useLocation } from 'react-router-dom';
import { useMe } from '../hooks/useMe';
import { useTheme } from '../theme/ThemeProvider';
import { useSession } from '../auth/session';
import { getRecent, onRecentChange } from '../lib/recent';
import type { RecentEntry } from '../lib/recent';
import { Button } from './ui/Button';
import { Icon } from './ui/Icon';
import type { IconName } from './ui/Icon';
import { Chip } from './ui/Chip';
import { ErrorBanner } from './ui/ErrorBanner';
import { BrandLogo } from './BrandLogo';
import { GlobalSearch } from './GlobalSearch';
import { PageErrorBoundary } from './PageErrorBoundary';
import { UploadDestinationDialog } from './UploadDestinationDialog';

const SIDEBAR_KEY = 'erp-attach.sidebar';
const RECENT_IN_NAV = 5;
const KIND_ICON: Record<RecentEntry['kind'], IconName> = { customer: 'building', site: 'briefcase', supplier: 'truck' };

function readCollapsed(): boolean {
  try {
    return localStorage.getItem(SIDEBAR_KEY) === 'collapsed';
  } catch {
    return false;
  }
}

function ThemeToggle({ labelled }: { labelled: boolean }) {
  const { theme, toggle } = useTheme();
  const next = theme === 'dark' ? 'light' : 'dark';
  const icon: IconName = theme === 'dark' ? 'sun' : 'moon';
  return (
    <button type="button" className="gsl-nav-item" onClick={toggle} aria-label={`Switch to ${next} mode`} title={`Switch to ${next} mode`}>
      <Icon name={icon} size={18} />
      {labelled ? <span className="gsl-nav-text">{theme === 'dark' ? 'Light mode' : 'Dark mode'}</span> : null}
    </button>
  );
}

function UserMenu() {
  const { me } = useMe();
  const { accountName, accountUpn, signOut } = useSession();
  const name = me?.displayName ?? accountName ?? accountUpn ?? 'Signed in';
  const upn = me?.upn ?? accountUpn ?? '';
  const initials = name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase())
    .join('');

  // The sidebar clips overflow, so an absolutely positioned pop was squeezed to the sidebar's width.
  // Fixed positioning beside the trigger escapes the clip; clamped so it stays on screen in the drawer.
  const [popStyle, setPopStyle] = useState<CSSProperties>();
  const place = (e: SyntheticEvent<HTMLDetailsElement>) => {
    const details = e.currentTarget;
    if (!details.open) return;
    const rect = details.querySelector('summary')!.getBoundingClientRect();
    const width = Math.min(288, window.innerWidth - 16);
    setPopStyle({
      position: 'fixed',
      width,
      left: Math.max(8, Math.min(rect.right + 8, window.innerWidth - width - 8)),
      bottom: Math.max(8, window.innerHeight - rect.bottom),
      right: 'auto',
    });
  };

  return (
    <details className="gsl-menu relative" onToggle={place}>
      <summary className="gsl-nav-item cursor-pointer" aria-label={`Account menu for ${name}`}>
        <span className="grid h-[18px] w-[18px] shrink-0 place-items-center rounded-full bg-gsl-red text-[9px] font-semibold text-white">
          {initials || <Icon name="user" size={12} />}
        </span>
        <span className="gsl-nav-text">
          {name}
          {upn ? <span className="gsl-nav-sub">{upn}</span> : null}
        </span>
      </summary>
      {/* Hidden until the first toggle has measured, so it never flashes clipped. */}
      <div className="gsl-menu-pop p-3" style={popStyle ?? { visibility: 'hidden' }}>
        <p className="truncate text-sm font-medium">{name}</p>
        {upn ? <p className="truncate text-xs text-muted-foreground">{upn}</p> : null}
        <div className="mt-3 flex flex-wrap gap-1">
          {me?.appRoles.length ? (
            me.appRoles.map((r) => (
              <Chip key={r} variant="neutral">
                {r}
              </Chip>
            ))
          ) : (
            <span className="text-xs text-muted-foreground">No app roles mapped from Fusion yet.</span>
          )}
        </div>
        {me && !me.fusionLinked ? (
          <p className="mt-2 text-xs text-muted-foreground">Not yet linked to a Fusion user; access is limited.</p>
        ) : null}
        <div className="mt-3 border-t border-border pt-3">
          <Button variant="secondary" size="sm" className="w-full" onClick={() => void signOut()}>
            Sign out
          </Button>
        </div>
      </div>
    </details>
  );
}

export function AppShell() {
  const { isRecordsAdmin, canAudit, error } = useMe();
  const location = useLocation();
  const [collapsed, setCollapsed] = useState<boolean>(() => readCollapsed());
  const [drawer, setDrawer] = useState(false);
  const [recent, setRecent] = useState<RecentEntry[]>(() => getRecent());
  const [pickedFiles, setPickedFiles] = useState<File[]>([]);
  const pickerRef = useRef<HTMLInputElement>(null);

  useEffect(() => onRecentChange(() => setRecent(getRecent())), []);
  useEffect(() => {
    setDrawer(false);
    setRecent(getRecent());
  }, [location.pathname]);

  // Escape closes the mobile drawer.
  useEffect(() => {
    if (!drawer) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setDrawer(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [drawer]);

  const toggleCollapsed = () => {
    setCollapsed((c) => {
      const next = !c;
      try {
        localStorage.setItem(SIDEBAR_KEY, next ? 'collapsed' : 'open');
      } catch {
        /* ignore */
      }
      return next;
    });
  };

  const onPicked = (e: ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    e.target.value = '';
    if (files.length > 0) setPickedFiles(files);
  };

  const labelled = !collapsed;
  const onHome = location.pathname === '/';

  return (
    <div className="gsl-shell" data-collapsed={collapsed ? 'true' : 'false'} data-drawer={drawer ? 'open' : 'closed'}>
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:fixed focus:left-3 focus:top-3 focus:z-[200] focus:rounded-lg focus:bg-card focus:px-3 focus:py-2"
      >
        Skip to content
      </a>

      {drawer ? <div className="gsl-scrim lg:hidden" onClick={() => setDrawer(false)} aria-hidden="true" /> : null}

      <aside className="gsl-sidebar" aria-label="Sidebar">
        <Link to="/" className="gsl-brand" aria-label="ERP Attach home">
          <BrandLogo variant="horizontal" className="gsl-brand-logo" />
          <BrandLogo variant="brandmark" className="gsl-brand-mark-img" alt="" />
          <span className="gsl-brand-app">ERP Attach</span>
        </Link>

        <div className="px-3 pb-1 pt-2">
          <input ref={pickerRef} type="file" multiple className="sr-only" onChange={onPicked} aria-label="Choose files to upload" data-testid="shell-upload-input" />
          <Button icon="upload" className="gsl-upload-cta w-full" onClick={() => pickerRef.current?.click()} aria-label="Upload files" title="Upload files">
            <span className="gsl-nav-text">Upload files</span>
          </Button>
        </div>

        <nav className="gsl-nav" aria-label="Primary">
          <NavLink to="/" end className="gsl-nav-item" title="Home">
            <Icon name="home" size={18} />
            <span className="gsl-nav-text">Home</span>
          </NavLink>
          <NavLink to="/browse" className="gsl-nav-item" title="Browse">
            <Icon name="folder" size={18} />
            <span className="gsl-nav-text">Browse</span>
          </NavLink>
          {isRecordsAdmin ? (
            <NavLink to="/admin" className="gsl-nav-item" title="Administration">
              <Icon name="settings" size={18} />
              <span className="gsl-nav-text">Administration</span>
            </NavLink>
          ) : null}
          {canAudit ? (
            <NavLink to="/audit" className="gsl-nav-item" title="Audit trail">
              <Icon name="history" size={18} />
              <span className="gsl-nav-text">Audit trail</span>
            </NavLink>
          ) : null}
        </nav>

        {recent.length > 0 ? (
          <nav className="gsl-nav gsl-nav-recent" aria-label="Recent records">
            <p className="gsl-nav-label">Recent</p>
            {recent.slice(0, RECENT_IN_NAV).map((r) => (
              <NavLink key={r.href} to={r.href} className="gsl-nav-item" title={`${r.label} (${r.number})`}>
                <Icon name={KIND_ICON[r.kind]} size={18} />
                <span className="gsl-nav-text">
                  {r.label}
                  <span className="gsl-nav-sub gsl-num">{r.number}</span>
                </span>
              </NavLink>
            ))}
          </nav>
        ) : null}

        <div className="gsl-sidebar-foot">
          <ThemeToggle labelled={labelled} />
          <UserMenu />
          <button
            type="button"
            className="gsl-nav-item hidden lg:flex"
            onClick={toggleCollapsed}
            aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            aria-expanded={!collapsed}
            title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            <Icon name={collapsed ? 'chevron-right' : 'chevron-left'} size={18} />
            <span className="gsl-nav-text">Collapse</span>
          </button>
        </div>
      </aside>

      <div className="min-w-0">
        <header className={`gsl-topbar ${onHome ? 'lg:hidden' : ''}`}>
          <Button variant="ghost" icon="menu" iconOnly aria-label="Open navigation" className="lg:hidden" onClick={() => setDrawer(true)} />
          {onHome ? <span className="flex-1" /> : <GlobalSearch />}
        </header>
        <main id="main" className="gsl-main">
          {error ? (
            <div className="mb-4">
              <ErrorBanner title="Your profile could not be loaded" error={error} />
            </div>
          ) : null}
          <PageErrorBoundary key={location.pathname}>
            <Outlet />
          </PageErrorBoundary>
        </main>
      </div>

      <UploadDestinationDialog open={pickedFiles.length > 0} files={pickedFiles} onClose={() => setPickedFiles([])} />
    </div>
  );
}
